import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C } from "./App";

const STATUS_BADGE = {
  active:    { bg: "#E8F5E9", color: "#2E7D32" },
  suspended: { bg: "#FFF8E1", color: "#B8860B" },
  banned:    { bg: "#FFEBEE", color: "#C62828" },
};

export function UsersView({ token }) {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [actingId, setActingId] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      authFetch(`/admin/users${search ? `?search=${encodeURIComponent(search)}` : ""}`, token),
      authFetch("/admin/groups", token).catch(() => ({ groups: [] })),
    ])
      .then(([u, g]) => { setUsers(u.users || []); setGroups(g.groups || []); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token, search]);

  useEffect(() => { load(); }, [load]);

  function assignGroup(userId, groupId) {
    setActingId(userId);
    authFetch(`/admin/users/${userId}/group`, token, { method: "PATCH", body: JSON.stringify({ group_id: groupId || null }) })
      .then(() => load())
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  function setStatus(userId, status) {
    setActingId(userId);
    authFetch(`/admin/users/${userId}/status`, token, { method: "PATCH", body: JSON.stringify({ status }) })
      .then(() => load())
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: 0 }}>Users</h1>
        <button onClick={() => setShowInvite(true)} style={{ background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>+ Add Staff Account</button>
      </div>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>Manage accounts, assign groups, suspend or ban</p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <input
        value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…"
        style={{ width: "100%", maxWidth: 320, padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, marginBottom: 16, boxSizing: "border-box" }}
      />

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.grayLight, textAlign: "left" }}>
                <Th>Name</Th><Th>Email</Th><Th>Group</Th><Th>Status</Th><Th>Joined</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const sb = STATUS_BADGE[u.status] || STATUS_BADGE.active;
                return (
                  <tr key={u.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <Td><b>{u.name}</b></Td>
                    <Td style={{ color: C.gray }}>{u.email}</Td>
                    <Td>
                      <select
                        value={u.group_id || ""} disabled={actingId === u.id}
                        onChange={(e) => assignGroup(u.id, e.target.value)}
                        style={{ fontSize: 12, padding: "4px 6px", border: `1px solid ${C.border}`, borderRadius: 6 }}
                      >
                        <option value="">— No group —</option>
                        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </Td>
                    <Td>
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: sb.color, background: sb.bg, padding: "2px 8px", borderRadius: 8 }}>{(u.status || "active").toUpperCase()}</span>
                    </Td>
                    <Td style={{ color: C.gray }}>{new Date(u.created_at).toLocaleDateString()}</Td>
                    <Td>
                      <div style={{ display: "flex", gap: 5 }}>
                        {u.status !== "suspended" && <SmallBtn onClick={() => setStatus(u.id, "suspended")} disabled={actingId === u.id} color={C.warn}>Suspend</SmallBtn>}
                        {u.status !== "banned" && <SmallBtn onClick={() => setStatus(u.id, "banned")} disabled={actingId === u.id} color={C.danger}>Ban</SmallBtn>}
                        {u.status !== "active" && <SmallBtn onClick={() => setStatus(u.id, "active")} disabled={actingId === u.id} color={C.success}>Reactivate</SmallBtn>}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && <InviteModal token={token} groups={groups} onClose={() => setShowInvite(false)} onCreated={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function Th({ children }) { return <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: C.gray, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children, style }) { return <td style={{ padding: "10px 14px", ...style }}>{children}</td>; }
function SmallBtn({ onClick, disabled, color, children }) {
  return <button onClick={onClick} disabled={disabled} style={{ background: "transparent", border: `1px solid ${color}55`, color, borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>{children}</button>;
}

// Mirrors the server-side policy in src/passwordPolicy.js — kept in sync
// manually since this is a separate frontend with no shared package.
function checkPasswordPolicy(password) {
  const errors = [];
  if (!password || password.length < 8) errors.push("at least 8 characters");
  if (!/[A-Z]/.test(password || "")) errors.push("one uppercase letter");
  if (!/[a-z]/.test(password || "")) errors.push("one lowercase letter");
  if (!/[0-9]/.test(password || "")) errors.push("one number");
  if (!/[^A-Za-z0-9]/.test(password || "")) errors.push("one symbol");
  return errors;
}

function InviteModal({ token, groups, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", group_id: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  function submit(e) {
    e.preventDefault();
    const pwErrors = checkPasswordPolicy(form.password);
    if (pwErrors.length) { setErr("Password must include: " + pwErrors.join(", ")); return; }
    setBusy(true); setErr("");
    authFetch("/admin/users", token, { method: "POST", body: JSON.stringify({ ...form, group_id: form.group_id || null }) })
      .then(onCreated)
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add Staff Account</div>
        {err && <div style={{ color: C.danger, fontSize: 12.5, marginBottom: 12 }}>{err}</div>}
        {["name", "email", "password"].map((f) => (
          <input
            key={f} type={f === "password" ? "password" : f === "email" ? "email" : "text"}
            placeholder={f === "password" ? "Min. 8 chars, upper+lower+number+symbol" : f[0].toUpperCase() + f.slice(1)}
            value={form[f]} required
            onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, marginBottom: 10 }}
          />
        ))}
        <select value={form.group_id} onChange={(e) => setForm((p) => ({ ...p, group_id: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          <option value="">— No group (no admin access) —</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={onClose} style={{ flex: 1, background: C.grayLight, border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={busy} style={{ flex: 1, background: C.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{busy ? "Creating…" : "Create Account"}</button>
        </div>
      </form>
    </div>
  );
}
